import { AfterViewInit, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { HttpParams, HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TimeagoModule } from 'ngx-timeago';

import { NgxPaginationModule } from 'ngx-pagination';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { NgSelectModule } from '@ng-select/ng-select';

import { ViewType } from '@/models/view-types';
import { Punk } from '@/models/graph';

import { DataService } from '@/services/data.service';
import { StateService } from '@/services/state.service';
import { PlatformService } from '@/services/platform.service';
import { Web3Service } from '@/services/web3.service';
import { TransactionTrackingService } from '@/services/transaction-tracking.service';
import { ThemeService } from '@/services/theme.service';

import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { WeiToEthPipe } from '@/pipes/wei-to-eth.pipe';
import { FormatCashPipe } from '@/pipes/format-cash.pipe';
import { FilterPipe } from '@/pipes/filter.pipe';
import { SortPipe } from '@/pipes/sort.pipe';
import { CountPipe } from '@/pipes/count.pipe';
import { PropertiesPipe } from '@/pipes/properties';
import { MarketplacePipe } from '@/pipes/marketplace';
import { UsdPipe } from '@/pipes/usd.pipe';
import { FilterLabelPipe } from '@/pipes/filter-label.pipe';
import { WalletAddressDirective } from '@/directives/wallet-address.directive';
import { parseEther, formatEther, TypedDataDomain } from 'viem';
import { buildMerkleTreeForPunks } from '@/utils/merkle-tree';
import { normalizeStashFundedForUi } from '@/utils/stash-funded';
import { nativeBidTypeLabelPlural } from '@/utils/native-market-bid-labels';

import { Filters, MarketplaceFilters, Sorts, SpecialTraitV1, SubgraphSortDirections, SubgraphSorts, WrappedFilters } from '@/models/pipes';

import { BehaviorSubject, map, Observable, shareReplay, Subject, Subscription, takeUntil, firstValueFrom } from 'rxjs';
import {environment} from "../../../environments/environment";
import { LoaderComponent } from '../icons/loader.component';
import { PUNKAUCTION_ADDRESS, TOKENWORKS_ADDRESS } from '@/constants/utils';
import {
  isPunksmarketNativeSale,
  punksmarketAppPunkUrl,
} from '@/constants/punksmarket-v1';
import { MarketplaceIconsComponent } from '@/components/marketplace-icons/marketplace-icons.component';
import { BidCreationModalComponent, BidType, CopyFromBidPayload } from '@/components/bid-creation-modal/bid-creation-modal.component';
import { BidDetailsModalComponent } from '@/components/bid-details-modal/bid-details-modal.component';
import { BidCopyConfirmationModalComponent } from '@/components/bid-copy-confirmation-modal/bid-copy-confirmation-modal.component';
import { TippyDirective } from '@/directives/tippy.directive';

@Component({
  selector: 'app-punk-grid-view',
  standalone: true,
  imports: [
    CommonModule,
    LoaderComponent,
    RouterModule,
    LazyLoadImageModule,
    NgxPaginationModule,
    NgSelectModule,
    FormsModule,
    TimeagoModule,

    WalletAddressDirective,

    TokenIdParsePipe,
    MarketplacePipe,
    WeiToEthPipe,
    FormatCashPipe,
    FilterPipe,
    SortPipe,
    CountPipe,
    PropertiesPipe,
    UsdPipe,
    FilterLabelPipe,
    MarketplaceIconsComponent,
    BidCreationModalComponent,
    BidDetailsModalComponent,
    BidCopyConfirmationModalComponent,
    TippyDirective,
  ],
  templateUrl: './punk-grid-view.component.html',
  styleUrls: ['./punk-grid-view.component.scss']
})

export class PunkGridViewComponent implements AfterViewInit, OnChanges, OnDestroy {

  TOKENWORKS_ADDRESS = TOKENWORKS_ADDRESS;
  PUNKAUCTION_ADDRESS = PUNKAUCTION_ADDRESS;
  // Pre-computed lowercase addresses to avoid repeated toLowerCase() calls in template
  readonly tokenworksAddressLower = TOKENWORKS_ADDRESS.toLowerCase();
  readonly punkauctionAddressLower = PUNKAUCTION_ADDRESS.toLowerCase();
  
  protected readonly environment = environment;
  private destroy$ = new Subject<void>();

  @Input() punkData: Punk[] | undefined | null;
  @Input() viewType: ViewType = 'medium';
  @Input() limit: number = 110;
  @Input() marketType: Filters = 'all';
  @Input() sort!: Sorts;
  @Input() address!: string;

  allSorts: { label: string, value: Sorts }[] = [
    { label: 'Price Low', value: 'price-low' },
    { label: 'Price High', value: 'price-high' },
    { label: 'Recent', value: 'recent' },
    { label: 'Token ID', value: 'id' }
  ];
  
  sorts: { label: string, value: Sorts }[] = this.allSorts
  private activeSort = new BehaviorSubject(this.sorts[0]);
  activeSort$ = this.activeSort.asObservable();
  activeSortModel: any = this.sorts[0];

  private activeFilters = new BehaviorSubject({});
  activeFilters$ = this.activeFilters.asObservable();
  activeFiltersModel: any = {};

  wrappedFilters: WrappedFilters[] = ['All', 'Wrapped', 'Unwrapped'];
  activeWrappedFilterModel: WrappedFilters = this.wrappedFilters[0];

  private activeWrappedFilters = new BehaviorSubject(this.wrappedFilters[0]);
  activeWrappedFilters$ = this.activeWrappedFilters.asObservable();

  currentPage: number = 1;
  filtersVisible: boolean = false;
  controlsFiltersVisible: boolean = false;
  
  traitCount: number | null = null;
  specialTraitV1!: SpecialTraitV1;
  hasActiveTraitFilters = false;

  // Collection bid creation state
  createCollectionBidModalActive: boolean = false;
  collectionBidAmount: string = '';
  stashAddress: string | null = null;
  stashDeployed: boolean = false;
  checkingStash: boolean = false;
  currentStep: 'check' | 'amount' | 'transfer' | 'sign' = 'check';
  isPunkGridViewTransactionActive = false;
  collectionBidExpiryOptions: { label: string; value: number }[] = [
    { label: '1 day', value: 1 },
    { label: '3 days', value: 3 },
    { label: '7 days', value: 7 },
    { label: '30 days', value: 30 },
    { label: '1 year', value: 365 }
  ];
  collectionBidExpiryModel = this.collectionBidExpiryOptions[2];
  stashLiquidityLoading: boolean = false;
  stashLiquidityTotalWei: bigint | null = null;
  stashLiquidityTotalEth: number | null = null;
  stashWithdrawInProgress: boolean = false;
  confidential: boolean = false;
  
  // Targeted bid selection state
  isTargetedBidMode: boolean = false;
  selectedPunkIds: number[] = [];
  // Set for O(1) lookup performance in templates (avoid method calls in render loop)
  selectedPunkIdsSet: Set<number> = new Set();
  punkIdInput: string = '';
  /** Stable reference for Tippy (inline object in template would recreate the tooltip every CD). */
  readonly addAllDisplayedPunksTippyOptions = {
    content:
      'Loads every sale row for the current filters if needed, then adds all matching punks to the custom bid',
    placement: 'top' as const,
  };
  readonly removeAllDisplayedPunksTippyOptions = {
    content:
      'Loads every sale row for the current filters (if needed), then removes all matching punks from the custom bid; other selected punks stay',
    placement: 'top' as const,
  };
  /** True when Remove All should be inactive (empty selection, no overlap with grid, etc.). */
  removeAllDisplayedPunksDisabled = true;
  /** True while fetching remaining sales pages before bulk remove. */
  removeAllBulkInProgress = false;
  /** True while fetching remaining sales pages before bulk add. */
  addAllBulkInProgress = false;

  private static readonly SALES_HISTORY_PAGE_SIZE = 200;
  /** First chunk of `selectedPunkIds` for the cart row (at most 27). */
  selectedPunkIdsCartVisible: number[] = [];
  /** Remaining selected IDs; length drives the "+N" tile and overflow panel. */
  selectedPunkIdsCartOverflow: number[] = [];
  selectedPunksOverflowPanelOpen = false;
  // Pre-computed properties to avoid method calls in template render loop
  selectedPunksText: string = 'select punks below';
  bidType: BidType = 'collection';
  filteredPunkIndices: number[] = [];
  activeTraitsList: string[] = [];
  filterTag: string = '';
  shouldShowLoadMoreButton: boolean = false;

  // Highest bid for the current filter criteria (matches ALL filtered punks)
  highestBidForCriteria: any | null = null;
  highestBidForCriteriaLoading: boolean = false;
  // Highest bid matching ANY of the filtered punks (secondary line)
  highestBidForCriteriaAny: any | null = null;
  highestBidForCriteriaAnyLoading: boolean = false;
  /** Banner label: full grid (no trait filters) vs filtered / targeted scope */
  highestBidBannerLabel: string = 'Highest collection bid:';
  highestBidDetailsModalActive: boolean = false;
  highestBidAnyDetailsModalActive: boolean = false;
  copyFromBidPayload: CopyFromBidPayload | null = null;
  copyConfirmationModalActive: boolean = false;
  copyConfirmationPunkIds: number[] = [];
  private highestBidSubscription: Subscription | null = null;
  private highestBidAnySubscription: Subscription | null = null;
  /** Avoid duplicate stash-bid API calls when criteria unchanged */
  private highestBidCriteriaKey: string | null = null;
  private highestBidAnyCriteriaKey: string | null = null;
  /** Stable array reference for pipe chain (special-trait filter only); otherwise same ref as punkData */
  filteredPunks: Punk[] = [];
  private static readonly MAX_FILTER_PUNK_IDS = 2000;
  /** Thumbnails shown in the selection cart before the "+N" overflow control. */
  private static readonly MAX_VISIBLE_SELECTED_PUNKS_IN_CART = 27;
  private static readonly COPY_BID_AMOUNT_INCREMENT_ETH = 0.01;
  private static readonly EXPIRY_DAY_OPTIONS = [1, 3, 7, 30, 365];

  /** Same transforms as the grid template pipe chain (keeps “Add All” in sync with visible rows). */
  private readonly propertiesPipe = new PropertiesPipe();
  private readonly filterPipe = new FilterPipe();
  private readonly marketplacePipe = new MarketplacePipe();
  private readonly sortPipe = new SortPipe();
  
  // Expose parseFloat and parseInt for template
  parseFloat = parseFloat;
  parseInt = parseInt;

  // Special trait tokenId mappings
  private readonly specialTraitTokenIds: Record<SpecialTraitV1, number[]> = {
    'Rainforest Punks': [1041, 1044, 1060, 3018, 3119, 3122, 3128, 3134, 3214, 3222,  
      3310, 3314, 3410, 4025, 4547, 4827, 4971, 5226, 5340,  
      5523, 5921, 6314, 6318, 6446, 6545, 6548, 6909, 6934,  
      7276, 7277, 7279, 7442, 7642, 7742, 8139, 8682, 8781, 8878, 9622],
    'Survivor Punks': [5237, 9146, 8611, 5041, 685, 2761, 2317, 5449, 2838, 3808, 7755, 3493]
  };

  // Special trait descriptions
  private readonly specialTraitDescriptions: Record<SpecialTraitV1, string> = {
    'Rainforest Punks': 'The 39 Cryptopunks V1 which were originally claimed by Matt Hall in 2017 and sold by him in 2022 have become part of the V1 story and are now collectively known as "Rainforest Punks."',
    'Survivor Punks': 'There are a number of Cryptopunks V1 that do not have a corresponding V2 Cryptopunk as the V2 version has been intentionally or accidentally sent to the burn address, the CryptoPunks contract address, or another unaccessible address.'
  };

  objectKeys = Object.keys;
  limitArr = Array.from({length: this.limit}, (_, i) => i);
  marketTitles: any = {
    all: 'All ' + environment.tokenName + 's',
    listings: environment.tokenName + 's for Sale',
    bids: nativeBidTypeLabelPlural,
    owned: environment.tokenName + 's Owned',
    'nftx-vault': 'NFTX Vault',
    sales: 'All ' + environment.tokenName + ' Sales'
  };
  nftxVaultAddress: string|null = null;
//  filteredItems$: Observable<Punk[]>;

  constructor(
    public dataSvc: DataService,
    public stateSvc: StateService,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private platformService: PlatformService,
    private web3Svc: Web3Service,
    public txTrackingSvc: TransactionTrackingService,
    public themeSvc: ThemeService,
    private http: HttpClient
  ) {
/*
    this.filteredItems$ = this.activeFilters$.pipe(
      map((filters) => {
        const filtered = this.propertiesPipe.transform(this.punkData ?? [], filters, this.traitCount);
        return filtered;
      }),
      shareReplay(1)
    );
    */
  }

  ngAfterViewInit(): void {
    this.nftxVaultAddress = environment.nftxVaultAddress
    const routeParams = this.route.snapshot.queryParams;

    if (Object.keys(routeParams).length && !routeParams?.address) {
      const keys = ["sex", "ears", "eyes", "hair", "emotion", "mouth", "beard", "face", "nose", "lips", "neck", "cheeks", "teeth", "skintone"];

      // Check keys to make sure they match the whitelist
      const filteredParams: any = {};
      for (let paramKey in routeParams) {
        if (keys.includes(paramKey)) {
          filteredParams[paramKey] = routeParams[paramKey];
        }
      }

      if (Object.keys(filteredParams).length !== Object.keys(routeParams).length) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: filteredParams,
          replaceUrl: true // this prevents navigation from adding a new entry to the browser's history
        });
      }

      this.activeFiltersModel = { ...filteredParams };
      this.activeFilters.next(this.activeFiltersModel);
      this.filtersVisible = true;
    }

    this.rebuildFilteredPunks();
    this.updateHasActiveFiltersFlag();
    this.updateComputedProperties();
  }  

  ngOnChanges(changes: SimpleChanges): void {
    // console.log(changes);
    if (changes?.punkData) {
      this.rebuildFilteredPunks();
    }
    if (changes?.sort?.currentValue) {
      const activeSort = this.sorts.find((sort) => sort.value === this.sort);
      this.setSort(activeSort);
      this.activeSortModel = activeSort;
    }
    if (changes?.marketType?.currentValue) {
      this.sorts = this.allSorts
      if (this.marketType === 'sales') {
        this.sorts = this.allSorts.filter(s => s.value !== 'id')
      }
    }
    // Update computed properties when inputs change
    if (changes?.punkData || changes?.marketType) {
      this.updateComputedProperties();
    }
  }

  ngOnDestroy(): void {
    this.highestBidSubscription?.unsubscribe();
    this.highestBidAnySubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.stateSvc.setSalesLoaded(0);
  }

  identify(index: number, item: any) {
    return item.id;
  }

  toggleMarketplaceIcons() {
    this.stateSvc.setHideMarketplaceIcons(!this.stateSvc.getHideMarketplaceIcons());
  }

  selectFilter($event: any, key: string): void {

    const filters = this.sanitizeFilters(this.activeFiltersModel);

    let params = new HttpParams();
    Object.keys(filters).map((key) => {
      if (filters[key]) params = params.append(key, filters[key]);
    });

    this.location.go(this.location.path().split('?')[0], params.toString());
    this.activeFiltersModel = { ...filters };
    this.activeFilters.next(this.activeFiltersModel);
    this.updateHasActiveFiltersFlag();
    this.updateComputedProperties();
  }

  // Filter punks by special trait
  filterBySpecialTrait(punks: Punk[] | undefined | null): Punk[] {
    if (!punks || !this.specialTraitV1) {
      return punks || [];
    }

    const selectedTokenIds = this.specialTraitTokenIds[this.specialTraitV1];
    if (!selectedTokenIds) {
      return punks;
    }

    return punks.filter(punk => {
      const tokenId = parseInt(punk.id);
      return selectedTokenIds.includes(tokenId);
    });
  }

  /**
   * Keeps a stable reference for the template pipe chain: without a special trait,
   * reuse punkData so pure pipes memoize; with a trait, cache the filtered slice.
   */
  private rebuildFilteredPunks(): void {
    if (!this.punkData) {
      this.filteredPunks = [];
      return;
    }
    if (!this.specialTraitV1) {
      this.filteredPunks = this.punkData;
      return;
    }
    this.filteredPunks = this.filterBySpecialTrait([...this.punkData]);
  }

  // Get description for selected special trait
  get selectedTraitDescription(): string {
    if (!this.specialTraitV1) {
      return '';
    }
    return this.specialTraitDescriptions[this.specialTraitV1] || '';
  }

  // Handle special trait selection change
  onSpecialTraitChange(): void {
    // Reset to first page when filter changes
    this.currentPage = 1;
    this.rebuildFilteredPunks();
    this.updateHasActiveFiltersFlag();
    this.updateComputedProperties();
  }

  onTraitCountChange(): void {
    this.updateHasActiveFiltersFlag();
    this.updateComputedProperties();
  }

  setSort($event: any): void {
    this.activeSort.next({ ...$event });
    if (this.marketType === 'sales') {
      this.stateSvc.currentSort.sort = 'blockTimestamp'
      this.stateSvc.currentSort.direction = 'desc'
      if (this.activeSort.value.value === 'price-high') {
        this.stateSvc.currentSort.sort = 'value'
      } else if (this.activeSort.value.value === 'price-low') {
        this.stateSvc.currentSort.sort = 'value'
        this.stateSvc.currentSort.direction = 'asc'
      }
      this.dataSvc.clearSalesData()
      this.dataSvc.getSalesData().pipe(
        takeUntil(this.destroy$)
      ).subscribe()
    }
  }

  setWrappedFilter($event: any): void {
    this.activeWrappedFilters.next($event);
  }

  pageChanged($event: any) {
    this.currentPage = $event;
    this.updateComputedProperties();
  }

  share(el: HTMLButtonElement): void {
    const buttonText = el.innerText;
    const buttonWidth = el.offsetWidth;

    el.innerText = 'Link Copied!';
    el.style.width = buttonWidth + 'px';
    el.style.textAlign = 'center';

    this.platformService.setTimeout(() => {
      el.innerText = buttonText;
      el.style.width = 'auto';
    }, 2000);

    const param = Date.now();
    this.dataSvc.fetchFloorPunksCard(param);
    const urlToShare = `${environment.siteUrl}/floorpunks?=${param}`;
    navigator.clipboard.writeText(urlToShare);
  }

  loadMore(): void {
    this.currentPage = this.currentPage + 1;
    this.updateComputedProperties();
  }

  loadMoreSales(): void {
    console.log('[PunkGridViewComponent] loadMoreSales called');
    this.currentPage = this.currentPage + 1;
    
    // Update UI state
    this.stateSvc.setLoadingMore(true);
    
    // Call DataService directly to load more data
    this.dataSvc.loadMoreSalesData().subscribe({
      next: (result) => {
        console.log('[PunkGridViewComponent] loadMoreSalesData completed');
        this.stateSvc.setLoadingMore(false);
        this.updateComputedProperties();
      },
      error: (error) => {
        console.error('[PunkGridViewComponent] loadMoreSalesData error:', error);
        this.stateSvc.setLoadingMore(false);
        this.updateComputedProperties();
      }
    });
  }

  shouldShowItem(index: number): boolean {
    return this.marketType === 'sales' || index < (this.currentPage * this.limit);
  }

  shouldShowLoadMore(): boolean {
    if (this.marketType === 'sales') {
      // Pour les ventes, vérifier les données filtrées
      const filteredData = this.getFilteredDataForLoadMore();
      return !!(filteredData && filteredData.length > 0);
    }
    
    // Pour les autres types de marché, vérifier les données filtrées
    const filteredData = this.getFilteredDataForLoadMore();
    return !!(filteredData && filteredData.length >= (this.currentPage * this.limit));
  }

  // Méthode pour obtenir les données filtrées selon la même logique que le template
  private getFilteredDataForLoadMore(): Punk[] {
    if (!this.punkData) {
      return [];
    }

    let filtered = [...this.punkData];

    // Appliquer le filtre de trait spécial si sélectionné
    if (this.specialTraitV1) {
      filtered = this.filterBySpecialTrait(filtered);
    }

    // Appliquer les filtres de propriétés (properties pipe)
    const activeFilters = this.activeFilters.value;
    if (activeFilters && Object.keys(activeFilters).length > 0) {
      filtered = this.applyPropertiesFilter(filtered, activeFilters, this.traitCount);
    }

    // Appliquer le filtre de marketplace
    // Note: Pour une solution complète, il faudrait s'abonner à l'observable
    // Pour l'instant, on utilise la valeur du modèle
    const marketplaceFilter = this.stateSvc.activeMarketplaceFilterModel;
    if (marketplaceFilter && marketplaceFilter !== 'All') {
      filtered = this.applyMarketplaceFilter(filtered, marketplaceFilter);
    }

    // Appliquer le filtre wrapped/unwrapped et marketType (filter pipe)
    const wrappedFilter = this.activeWrappedFilters.value;
    filtered = this.applyFilterPipe(filtered, this.marketType, wrappedFilter, marketplaceFilter);

    return filtered;
  }

  // Méthode helper pour appliquer le filtre properties
  private applyPropertiesFilter(punks: Punk[], filters: any, traitCount?: number | null): Punk[] {
    if (!punks?.length) return [];
    if (!filters || !Object.keys(filters).length) return punks;

    // Filtrer par nombre de traits
    if (traitCount !== undefined && traitCount !== null) {
      punks = punks.filter((punk) => (punk.attributes?.length - 2) === traitCount);
    }

    const filtersLength = Object.keys(filters).length;
    
    return punks.filter((punk: Punk) => {
      const attrs = punk.attributes;
      if (attrs) {
        const found = attrs.filter((attr) => {
          const key = attr?.k?.replace(/ /g, '-')?.toLowerCase();
          const val = attr?.v?.replace(/ /g, '-')?.toLowerCase();
          return filters[key] === val;
        });
        return found.length === filtersLength;
      }
      return false;
    });
  }

  // Méthode helper pour appliquer le filtre marketplace
  private applyMarketplaceFilter(punks: Punk[], marketplaceFilter: string): Punk[] {
    if (!punks?.length) return [];
    if (!marketplaceFilter || marketplaceFilter === 'All') return punks;

    return punks.filter((item: any) => {
      // For sales data, check the platform field directly
      if (item.platform !== undefined) {
        if (marketplaceFilter === 'Native') return !item.platform || item.platform === 'native' || item.platform === 'larvalabs';
        if (marketplaceFilter === 'Opensea') return item.platform === 'opensea';
        if (marketplaceFilter === 'Blur') return item.platform === 'blur';
        if (marketplaceFilter === 'NFTX') return item.platform === 'nftx';
        if (marketplaceFilter === 'Gondi') return item.platform === 'gondi';
      }
      
      // For listings data, check the listing.source.domain
      if (item.listing?.source) {
        if (marketplaceFilter === 'Native') return !item.listing?.source;
        if (marketplaceFilter === 'Opensea') return item.listing?.source?.domain === 'opensea.io';
        if (marketplaceFilter === 'Blur') return item.listing?.source?.domain === 'blur.io';
        if (marketplaceFilter === 'NFTX') return item.listing?.source?.domain === 'nftx.io';
        if (marketplaceFilter === 'Gondi') return item.listing?.source?.domain === 'gondi.xyz';
      }
      
      // If no platform info, show for Native filter
      if (marketplaceFilter === 'Native') return true;
      
      return false;
    });
  }

  // Méthode helper pour appliquer le filtre filter pipe
  private applyFilterPipe(punks: Punk[], marketType: Filters, wrappedFilter?: WrappedFilters, marketplaceFilter?: MarketplaceFilters): Punk[] {
    if (!punks?.length) return [];

    let filtered = [...punks];

    // Filtrer les items avec des valeurs valides
    filtered = filtered.filter((res) => res.listing?.value !== '0' && res.bid?.value !== '0');

    // Appliquer le filtre wrapped/unwrapped
    if (wrappedFilter) {
      if (wrappedFilter === 'Wrapped') filtered = filtered.filter((item: any) => item.wrapped);
      if (wrappedFilter === 'Unwrapped') filtered = filtered.filter((item: any) => !item.wrapped);
    }

    // Appliquer le filtre marketplace pour les listings
    if (marketplaceFilter) {
      if (marketplaceFilter === 'Native') filtered = filtered.filter((item: any) => item.listing);
      if (marketplaceFilter === 'Opensea') filtered = filtered.filter((item: any) => !item.listing);
    }

    // Appliquer le filtre de type de marché
    if (marketType === 'listings') return filtered.filter((item: any) => item.listing && item.listing.value !== '0');
    if (marketType === 'bids') return filtered.filter((item: any) => item.bid && item.bid.value !== '0');

    return filtered;
  }

  handleLoadMore(): void {
    if (this.marketType === 'sales') {
      this.loadMoreSales();
    } else {
      this.loadMore();
    }
  }

  // Copy punk JSON data to clipboard
  copyPunkJson(punk: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    try {
      const jsonString = JSON.stringify(punk, null, 2);
      navigator.clipboard.writeText(jsonString).then(() => {
        // Show temporary feedback
        const target = event.target as HTMLElement;
        const originalText = target.textContent;
        target.textContent = 'Copied!';
        target.style.color = '#00ff00';
        
        setTimeout(() => {
          target.textContent = originalText;
          target.style.color = '';
        }, 1000);
      }).catch(err => {
        console.error('Failed to copy punk JSON:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = jsonString;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      });
    } catch (error) {
      console.error('Error serializing punk data:', error);
    }
  }

  private sanitizeFilters(filters: Record<string, any>): Record<string, any> {
    const sanitized = { ...(filters || {}) };
    Object.keys(sanitized).forEach((key) => {
      if (sanitized[key] === null || sanitized[key] === undefined || sanitized[key] === '') {
        delete sanitized[key];
      }
    });
    return sanitized;
  }

  private updateHasActiveFiltersFlag(): void {
    const filters = this.sanitizeFilters(this.activeFiltersModel);
    const hasFilters =
      Object.keys(filters).length > 0 ||
      this.traitCount !== undefined && this.traitCount !== null ||
      !!this.specialTraitV1;

    this.hasActiveTraitFilters = hasFilters;
  }

  // Update all computed properties to avoid method calls in template render loop
  private updateComputedProperties(): void {
    // Update selected punks text
    if (this.selectedPunkIds.length === 0) {
      this.selectedPunksText = 'select punks below';
    } else {
      this.selectedPunksText = `${this.selectedPunkIds.length} punk${this.selectedPunkIds.length !== 1 ? 's' : ''} selected`;
    }

    // Update bid type
    if (this.isTargetedBidMode && this.selectedPunkIds.length > 0) {
      this.bidType = 'targeted';
    } else if (this.hasActiveTraitFilters) {
      this.bidType = 'trait';
    } else {
      this.bidType = 'collection';
    }

    this.highestBidBannerLabel =
      this.bidType === 'collection' ? 'Highest collection bid:' : 'Highest stash bid';

    // Update filtered punk indices
    this.filteredPunkIndices = this.getFilteredPunkIndices();

    // Update active traits list
    this.activeTraitsList = this.getActiveTraitsList();

    // Update filter tag
    this.filterTag = this.getFilterTag();

    // Update should show load more
    this.shouldShowLoadMoreButton = this.shouldShowLoadMore();

    // Selection cart: cap visible thumbnails; overflow opens in a scrollable panel
    const maxCart = PunkGridViewComponent.MAX_VISIBLE_SELECTED_PUNKS_IN_CART;
    const selN = this.selectedPunkIds.length;
    if (selN <= maxCart) {
      this.selectedPunkIdsCartVisible = this.selectedPunkIds;
      this.selectedPunkIdsCartOverflow = [];
      this.selectedPunksOverflowPanelOpen = false;
    } else {
      this.selectedPunkIdsCartVisible = this.selectedPunkIds.slice(0, maxCart);
      this.selectedPunkIdsCartOverflow = this.selectedPunkIds.slice(maxCart);
    }

    // Fetch highest bid for current criteria
    this.fetchHighestBidForCriteria();
    this.fetchHighestBidForCriteriaAny();

    this.refreshRemoveAllDisplayedPunksDisabled();
  }

  private extractPunkIdsFromRows(rows: Punk[]): number[] {
    const ids: number[] = [];
    for (const item of rows) {
      const punkId = parseInt(item.id, 10);
      if (Number.isFinite(punkId)) {
        ids.push(punkId);
      }
    }
    return ids;
  }

  /**
   * Grid rows for a punk source using the same pipes as the template (`filteredPunks` for display).
   */
  private buildGridRowsFromSource(source: Punk[], paginated: boolean): Punk[] {
    if (!source.length) {
      return [];
    }

    const activeFiltersArg = { ...this.activeFilters.value };
    let rows: Punk[];

    if (this.marketType === 'sales') {
      rows = this.propertiesPipe.transform([...source], activeFiltersArg, this.traitCount);
      if (paginated) {
        rows = rows.filter((_, i) => i < this.currentPage * this.limit);
      }
    } else {
      let value = [...source];
      value = this.propertiesPipe.transform(value, activeFiltersArg, this.traitCount);
      value = this.filterPipe.transform(value, this.marketType, this.activeWrappedFilters.value);
      value = this.marketplacePipe.transform(value, this.stateSvc.getMarketplaceFilter());
      const sortVal =
        (this.activeSort.value?.value ?? this.sorts[0].value) as Sorts;
      rows = this.sortPipe.transform(value, sortVal, this.marketType);
      if (paginated) {
        rows = rows.filter((_, index) => this.shouldShowItem(index));
      }
    }

    return rows;
  }

  /**
   * Punk IDs currently shown in the grid (same pipeline as the template and Add All).
   */
  private getDisplayedGridPunkIds(): number[] {
    if (!this.isTargetedBidMode || !this.filteredPunks.length) {
      return [];
    }
    return this.extractPunkIdsFromRows(
      this.buildGridRowsFromSource(this.filteredPunks, true)
    );
  }

  /**
   * All punk IDs matching the current grid filters (no pagination). For sales, pass the loaded
   * sales array (e.g. from `salesData`) so it reflects data after bulk fetch.
   */
  private getAllFilteredViewPunkIdsFromSource(source: Punk[]): number[] {
    if (!this.isTargetedBidMode || !source.length) {
      return [];
    }
    return this.extractPunkIdsFromRows(this.buildGridRowsFromSource(source, false));
  }

  private getSalesPipelineSourcePunks(): Punk[] {
    const raw = this.dataSvc.getSalesDataValue() ?? [];
    if (!raw.length) {
      return [];
    }
    return this.specialTraitV1 ? this.filterBySpecialTrait([...raw]) : raw;
  }

  private async ensureAllSalesPagesLoaded(): Promise<void> {
    if (this.marketType !== 'sales') {
      return;
    }
    for (;;) {
      const before = this.stateSvc.getSalesLoaded();
      await firstValueFrom(this.dataSvc.loadMoreSalesData());
      const after = this.stateSvc.getSalesLoaded();
      const delta = after - before;
      if (delta < PunkGridViewComponent.SALES_HISTORY_PAGE_SIZE) {
        break;
      }
    }
  }

  private refreshRemoveAllDisplayedPunksDisabled(): void {
    if (!this.isTargetedBidMode || this.selectedPunkIds.length === 0) {
      this.removeAllDisplayedPunksDisabled = true;
      return;
    }
    const displayed = this.getDisplayedGridPunkIds();
    const sel = this.selectedPunkIdsSet;
    this.removeAllDisplayedPunksDisabled = !displayed.some((id) => sel.has(id));
  }

  private fetchHighestBidForCriteria(): void {
    const shouldFetch =
      (this.marketType === 'all' || this.marketType === 'listings') &&
      environment.featureFlags?.STASH_BID_SUPPORT !== false &&
      (environment.collection === 'cryptopunks' || environment.collection === 'cryptopunks-v1') &&
      this.filteredPunkIndices.length > 0;

    if (!shouldFetch) {
      this.highestBidSubscription?.unsubscribe();
      this.highestBidSubscription = null;
      this.highestBidCriteriaKey = null;
      this.highestBidForCriteria = null;
      this.highestBidForCriteriaLoading = false;
      return;
    }

    const punkData = this.punkData;
    const isFullCollectionScope =
      this.bidType === 'collection' &&
      !!punkData?.length &&
      this.filteredPunkIndices.length === punkData.length;
    const maxId =
      isFullCollectionScope && punkData?.length
        ? Math.max(...punkData.map((p) => parseInt(p.id, 10)))
        : 0;
    const idsSlice = this.filteredPunkIndices.slice(
      0,
      PunkGridViewComponent.MAX_FILTER_PUNK_IDS
    );
    const criteriaKey = [
      this.bidType,
      isFullCollectionScope ? 'full' : 'ids',
      maxId,
      this.filteredPunkIndices.length,
      punkData?.length ?? 0,
      isFullCollectionScope ? '' : idsSlice.join(','),
    ].join('|');

    if (criteriaKey === this.highestBidCriteriaKey) {
      return;
    }

    this.highestBidCriteriaKey = criteriaKey;
    this.highestBidSubscription?.unsubscribe();

    const stashParams =
      isFullCollectionScope && punkData
        ? {
            filterPunkIdRanges: `0-${maxId}`,
            filterPunkIdsMatch: 'all' as const,
          }
        : {
            filterPunkIds: idsSlice,
            filterPunkIdsMatch: 'all' as const,
          };

    const marketBidType =
      this.bidType === 'collection'
        ? 'collection'
        : this.bidType === 'trait'
          ? 'trait'
          : this.bidType === 'targeted'
            ? 'custom'
            : undefined;

    this.highestBidForCriteriaLoading = true;
    this.highestBidSubscription = this.dataSvc.fetchAllStashBids({
      ...stashParams,
      ...(marketBidType ? { filterMarketBidType: marketBidType } : {}),
      excludeUnfunded: true,
      sortBy: 'amount',
      sortOrder: 'desc',
      page: 1,
      limit: 1,
    }).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: (response) => {
        const top = response.bids?.[0] ?? null;
        const minEth = environment.highestGridBidDisplayMinEth;
        const amountEth = top != null ? Number(top.bid_amount_eth) : NaN;
        const belowConfiguredMin =
          minEth != null &&
          minEth > 0 &&
          top != null &&
          (!Number.isFinite(amountEth) || amountEth < minEth);
        this.highestBidForCriteria =
          top && !belowConfiguredMin
            ? {
                ...top,
                stash_funded: normalizeStashFundedForUi(top.stash_funded),
              }
            : null;
        this.highestBidForCriteriaLoading = false;
      },
      error: () => {
        this.highestBidForCriteria = null;
        this.highestBidForCriteriaLoading = false;
        this.highestBidCriteriaKey = null;
      },
    });
  }

  private fetchHighestBidForCriteriaAny(): void {
    const shouldFetch =
      (this.marketType === 'all' || this.marketType === 'listings') &&
      environment.featureFlags?.STASH_BID_SUPPORT !== false &&
      (environment.collection === 'cryptopunks' || environment.collection === 'cryptopunks-v1') &&
      this.filteredPunkIndices.length > 0 &&
      this.hasActiveTraitFilters;

    if (!shouldFetch) {
      this.highestBidAnySubscription?.unsubscribe();
      this.highestBidAnySubscription = null;
      this.highestBidAnyCriteriaKey = null;
      this.highestBidForCriteriaAny = null;
      this.highestBidForCriteriaAnyLoading = false;
      return;
    }

    const punkData = this.punkData;
    const idsSlice = this.filteredPunkIndices.slice(
      0,
      PunkGridViewComponent.MAX_FILTER_PUNK_IDS
    );
    const criteriaKey = [
      'any',
      this.filteredPunkIndices.length,
      punkData?.length ?? 0,
      idsSlice.join(','),
    ].join('|');

    if (criteriaKey === this.highestBidAnyCriteriaKey) {
      return;
    }

    this.highestBidAnyCriteriaKey = criteriaKey;
    this.highestBidAnySubscription?.unsubscribe();

    const stashParams = {
      filterPunkIds: idsSlice,
      filterPunkIdsMatch: 'any' as const,
    };

    this.highestBidForCriteriaAnyLoading = true;
    this.highestBidAnySubscription = this.dataSvc.fetchAllStashBids({
      ...stashParams,
      filterMarketBidType: 'trait',
      excludeUnfunded: true,
      sortBy: 'amount',
      sortOrder: 'desc',
      page: 1,
      limit: 1,
    }).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: (response) => {
        const top = response.bids?.[0] ?? null;
        const minEth = environment.highestGridBidDisplayMinEth;
        const amountEth = top != null ? Number(top.bid_amount_eth) : NaN;
        const belowConfiguredMin =
          minEth != null &&
          minEth > 0 &&
          top != null &&
          (!Number.isFinite(amountEth) || amountEth < minEth);
        this.highestBidForCriteriaAny =
          top && !belowConfiguredMin
            ? {
                ...top,
                stash_funded: normalizeStashFundedForUi(top.stash_funded),
              }
            : null;
        this.highestBidForCriteriaAnyLoading = false;
      },
      error: () => {
        this.highestBidForCriteriaAny = null;
        this.highestBidForCriteriaAnyLoading = false;
        this.highestBidAnyCriteriaKey = null;
      },
    });
  }

  // Get filtered punk indices based on active trait filters only
  // (for collection bids, we only filter by traits, not by marketplace or wrapped status)
  getFilteredPunkIndices(): number[] {
    if (!this.punkData) {
      return [];
    }

    let filtered = [...this.punkData];

    // Apply special trait filter if selected
    if (this.specialTraitV1) {
      filtered = this.filterBySpecialTrait(filtered);
    }

    // Apply properties filters (trait filters and traitCount)
    const activeFilters = this.activeFilters.value;
    if (activeFilters && Object.keys(activeFilters).length > 0) {
      filtered = this.applyPropertiesFilter(filtered, activeFilters, this.traitCount);
    } else if (this.traitCount !== undefined && this.traitCount !== null) {
      // If only traitCount is set without other filters
      filtered = filtered.filter((punk) => (punk.attributes?.length - 2) === this.traitCount);
    }

    // Extract token IDs and convert to numbers
    return filtered.map(punk => parseInt(punk.id));
  }

  // Generate filter tag string in URL query format
  getFilterTag(): string {
    if (!this.hasActiveTraitFilters) {
      return '';
    }
    let httpParams = new HttpParams();

    // Add activeFiltersModel parameters
    const filters = this.sanitizeFilters(this.activeFiltersModel);
    
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        httpParams = httpParams.append(key, filters[key]);
      }
    });

    // Add traitCount if defined
    if (this.traitCount !== undefined && this.traitCount !== null) {
      httpParams = httpParams.append('traitCount', this.traitCount.toString());
    }

    // Add specialTraitV1 if defined
    if (this.specialTraitV1) {
      httpParams = httpParams.append('specialTrait', this.specialTraitV1);
    }

    // Convert to string and remove the '?' prefix if present
    const queryString = httpParams.toString();
    return queryString;
  }

  // Get bid type label for display in the modal
  getBidTypeLabel(): string {
    if (this.isTargetedBidMode && this.selectedPunkIds.length > 0) {
      return 'Custom bid';
    }
    if (this.hasActiveTraitFilters) {
      return 'Trait bid';
    }
    return 'Collection (unfiltered)';
  }

  // Get list of active trait filters for display
  getActiveTraitsList(): string[] {
    const traits: string[] = [];
    
    // Add special trait if selected
    if (this.specialTraitV1) {
      traits.push(this.specialTraitV1);
    }
    
    // Add trait count if defined
    if (this.traitCount !== undefined && this.traitCount !== null) {
      traits.push(`${this.traitCount} trait${this.traitCount !== 1 ? 's' : ''}`);
    }
    
    // Add active filters from activeFiltersModel
    const filters = this.sanitizeFilters(this.activeFiltersModel);
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        // Format key-value pair: capitalize key and value
        const formattedKey = key.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const formattedValue = filters[key].replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
        traits.push(`${formattedKey}: ${formattedValue}`);
      }
    });
    
    return traits;
  }

  // Get bid type for the modal component
  getBidType(): BidType {
    if (this.isTargetedBidMode && this.selectedPunkIds.length > 0) {
      return 'targeted';
    }
    if (this.hasActiveTraitFilters) {
      return 'trait';
    }
    return 'collection';
  }

  // Handler for when a bid is created successfully
  onBidCreated(): void {
    console.log('[PunkGridViewComponent] Bid created successfully');
    this.closeCreateCollectionBid();
    if (this.isTargetedBidMode) {
      this.cancelTargetedBidMode();
    }
  }

  // Collection bid creation methods
  async openCreateCollectionBid(): Promise<void> {
    this.createCollectionBidModalActive = true;
  }

  async checkStashStatus(): Promise<void> {
    try {
      const walletAddress = this.stateSvc.getWalletAddress();
      if (!walletAddress) {
        throw new Error('No wallet connected');
      }

      // Get stash address
      try {
        this.stashAddress = await this.web3Svc.getStashAddressFor(walletAddress);
      } catch (error) {
        console.log('[PunkGridViewComponent] No stash address found, will need to deploy');
        this.stashAddress = null;
      }

      // Check if deployed
      if (this.stashAddress) {
        this.stashDeployed = await this.web3Svc.checkStashDeployed(walletAddress);
      } else {
        this.stashDeployed = false;
      }

      if (this.stashAddress && this.stashDeployed) {
        await this.loadStashAvailableLiquidity();
      } else {
        this.clearStashLiquidityState();
      }

      this.checkingStash = false;
    } catch (error) {
      console.error('[PunkGridViewComponent] Error checking stash status:', error);
      this.checkingStash = false;
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>Error checking stash status.</p>');
    }
  }

  private clearStashLiquidityState(): void {
    this.stashLiquidityTotalWei = null;
    this.stashLiquidityTotalEth = null;
    this.stashLiquidityLoading = false;
  }

  private async loadStashAvailableLiquidity(): Promise<void> {
    if (!this.stashAddress) {
      this.clearStashLiquidityState();
      return;
    }

    this.stashLiquidityLoading = true;
    try {
      const totalWei = await this.web3Svc.getStashAvailableLiquidity(this.stashAddress);
      this.stashLiquidityTotalWei = totalWei;
      this.stashLiquidityTotalEth = parseFloat(formatEther(totalWei));
    } catch (error) {
      console.error('[PunkGridViewComponent] Error fetching stash liquidity:', error);
      this.clearStashLiquidityState();
    } finally {
      this.stashLiquidityLoading = false;
    }
  }

  async deployStash(): Promise<void> {
    try {
      const walletAddress = this.stateSvc.getWalletAddress();
      if (!walletAddress) {
        throw new Error('No wallet connected');
      }

      this.isPunkGridViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the stash deployment transaction in your wallet.</p>');

      const hash = await this.web3Svc.deployStash(walletAddress);
      if (!hash) {
        throw new Error('Transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your stash deployment transaction is being processed.</p>');

      const receipt = await this.web3Svc.waitForTransaction(hash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt, '<p>Your stash has been deployed successfully!</p>');

      // Update stash status
      await this.checkStashStatus();
      
      // Close transaction modal and proceed to next step
      setTimeout(() => {
        this.txTrackingSvc.closeTxModal();
        this.isPunkGridViewTransactionActive = false;
        this.proceedToAmountInput();
      }, 2000);
    } catch (error) {
      console.error('[PunkGridViewComponent] Error deploying stash:', error);
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>Error deploying stash.</p>');
      setTimeout(() => {
        this.isPunkGridViewTransactionActive = false;
      }, 3000);
    }
  }

  proceedToAmountInput(): void {
    this.currentStep = 'amount';
  }

  async transferFundsToStash(): Promise<void> {
    try {
      if (!this.collectionBidAmount || parseFloat(this.collectionBidAmount) <= 0) {
        throw new Error('Invalid bid amount');
      }

      if (!this.stashAddress) {
        throw new Error('Stash address not available');
      }

      const walletAddress = this.stateSvc.getWalletAddress();
      if (!walletAddress) {
        throw new Error('No wallet connected');
      }

      // Convert ETH to wei - ensure it's a string
      const amountString = String(this.collectionBidAmount).trim();
      if (!amountString || isNaN(parseFloat(amountString))) {
        throw new Error('Invalid bid amount format');
      }
      const amountWeiBigInt = parseEther(amountString);
      const availableWei = this.stashLiquidityTotalWei ?? BigInt(0);

      if (availableWei >= amountWeiBigInt) {
        console.log('[PunkGridViewComponent] Stash already has sufficient liquidity; skipping transfer step.');
        this.currentStep = 'sign';
        return;
      }

      const shortfallWei = amountWeiBigInt - availableWei;
      this.currentStep = 'transfer';
      const shortfallWeiString = shortfallWei.toString();

      this.isPunkGridViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the transfer transaction in your wallet.</p>');

      const hash = await this.web3Svc.sendEthToAddress(this.stashAddress, shortfallWeiString);
      if (!hash) {
        throw new Error('Transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your transfer transaction is being processed.</p>');

      const receipt = await this.web3Svc.waitForTransaction(hash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt, '<p>Funds have been transferred to your stash successfully!</p>');

      try {
        await this.loadStashAvailableLiquidity();
      } catch (liquidityError) {
        console.error('[PunkGridViewComponent] Error refreshing stash liquidity after transfer:', liquidityError);
      }

      // Close transaction modal and proceed to signature step
      setTimeout(() => {
        this.txTrackingSvc.closeTxModal();
        this.isPunkGridViewTransactionActive = false;
        this.currentStep = 'sign';
      }, 2000);
    } catch (error) {
      console.error('[PunkGridViewComponent] Error transferring funds:', error);
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>Error transferring funds to stash.</p>');
      setTimeout(() => {
        this.isPunkGridViewTransactionActive = false;
      }, 3000);
    }
  }

  async withdrawAvailableEth(): Promise<void> {
    try {
      if (!this.stashAddress) {
        throw new Error('Stash address not available');
      }

      const stashAddress = this.stashAddress;
      this.stashWithdrawInProgress = true;
      this.closeCreateCollectionBid();

      const availableEthWei = await this.web3Svc.getStashAvailableEth(stashAddress);
      if (!availableEthWei || availableEthWei === BigInt(0)) {
        this.txTrackingSvc.setErrorTransactionMessage(
          { message: 'No ETH available to withdraw.' },
          '<p>No ETH available to withdraw from your stash.</p>'
        );
        return;
      }

      this.isPunkGridViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the withdraw transaction in your wallet.</p>');

      const hash = await this.web3Svc.withdrawEthFromStash(stashAddress, availableEthWei);
      if (!hash) {
        throw new Error('Transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your withdraw transaction is being processed.</p>');

      const receipt = await this.web3Svc.waitForTransaction(hash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt, '<p>ETH withdrawn from your stash successfully!</p>');
      setTimeout(() => {
        this.isPunkGridViewTransactionActive = false;
      }, 3000);
    } catch (error) {
      console.error('[PunkGridViewComponent] Error withdrawing ETH from stash:', error);
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>Error withdrawing ETH from stash.</p>');
      setTimeout(() => {
        this.isPunkGridViewTransactionActive = false;
      }, 3000);
    } finally {
      this.stashWithdrawInProgress = false;
    }
  }

  private async getNextCollectionBidAccountNonce(walletAddress: string): Promise<number> {
    const padding = 0;
    try {
      const onchainNonce = await this.web3Svc.getCollectionBidAccountNonce(walletAddress);
      return onchainNonce + padding;
    } catch (error) {
      console.warn('[PunkGridViewComponent] Unable to fetch on-chain account nonce, using padding only.', error);
      return padding;
    }
  }

  closeCreateCollectionBid(): void {
    this.createCollectionBidModalActive = false;
    this.collectionBidAmount = '';
    this.stashAddress = null;
    this.stashDeployed = false;
    this.checkingStash = false;
    this.currentStep = 'check';
    this.collectionBidExpiryModel = this.collectionBidExpiryOptions[2];
    this.confidential = false;
    this.clearStashLiquidityState();
    this.copyFromBidPayload = null;
    this.copyConfirmationModalActive = false;
    this.copyConfirmationPunkIds = [];
    // Don't clear targeted bid mode or selection when closing modal
    // User might want to adjust selection and try again
  }

  onCopyHighestBid(bid: any): void {
    if (!bid) return;

    const bidId = bid.id != null ? String(bid.id) : '';
    if (!bidId) {
      this.buildCopyHighestBidPayload(bid, null);
      return;
    }

    this.dataSvc.fetchBidDetailsWithPunkIndices(bidId).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: (bidDetails: any) => {
        this.buildCopyHighestBidPayload(bid, bidDetails);
      },
      error: () => {
        this.buildCopyHighestBidPayload(bid, null);
      },
    });
  }

  private extractPunkIdsFromBidDetails(bidDetails: any): number[] {
    if (!bidDetails?.parsed_punk_indices) return [];
    if (bidDetails.parsed_punk_indices === 'all') return [];
    if (Array.isArray(bidDetails.parsed_punk_indices)) return bidDetails.parsed_punk_indices;
    return [];
  }

  /**
   * Same branching as market/dashboard: review punks modal when bid is not collection-wide and has IDs.
   */
  private buildCopyHighestBidPayload(bid: any, bidDetails: any): void {
    const modalBidType: BidType = bid.bidType === 'custom' ? 'targeted'
      : (bid.bidType === 'owner' || bid.bidType === 'trait' || bid.bidType === 'collection')
        ? bid.bidType
        : 'collection';

    let punkIndices: number[] = [];
    let selectedPunkIds: number[] = [];

    const detailPunkIds = this.extractPunkIdsFromBidDetails(bidDetails);

    if (modalBidType === 'owner' || modalBidType === 'trait') {
      punkIndices = detailPunkIds.length > 0 ? detailPunkIds : (bid.punkIds ?? bid.punkIndices ?? []);
    } else if (modalBidType === 'targeted') {
      if (detailPunkIds.length > 0) {
        selectedPunkIds = detailPunkIds;
      } else {
        const rawIds = bid.selected_punk_ids ?? bid.punkIds ?? bid.punkIndices ?? [];
        selectedPunkIds = Array.isArray(rawIds)
          ? rawIds
          : typeof rawIds === 'string'
            ? rawIds.split(',').map((s: string) => parseInt(s.trim(), 10)).filter((n: number) => !isNaN(n))
            : [];
      }
    }

    let expiryDays: number | undefined;
    if (bid.expires_at && bid.created_at) {
      const diffMs = new Date(bid.expires_at).getTime() - new Date(bid.created_at).getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays > 0) {
        expiryDays = PunkGridViewComponent.EXPIRY_DAY_OPTIONS.reduce((prev, curr) =>
          Math.abs(curr - diffDays) < Math.abs(prev - diffDays) ? curr : prev
        );
      }
    }

    this.copyFromBidPayload = {
      amountEth: bid.bid_amount_eth + PunkGridViewComponent.COPY_BID_AMOUNT_INCREMENT_ETH,
      expiryDays,
      confidential: bid.confidential ?? false,
      bidType: modalBidType,
      ownerAddress: bid.ownerAddress ?? null,
      punkIndices,
      selectedPunkIds,
      activeTraits: bid.decodedTags ?? [],
      filterTag: bid.tag ?? '',
    };

    const allPunkIds = [...punkIndices, ...selectedPunkIds];
    if (modalBidType !== 'collection' && allPunkIds.length > 0) {
      this.copyConfirmationPunkIds = allPunkIds;
      this.copyConfirmationModalActive = true;
    } else {
      this.createCollectionBidModalActive = true;
    }
  }

  onCopyHighestBidConfirmationConfirmed(): void {
    this.copyConfirmationModalActive = false;
    this.copyConfirmationPunkIds = [];
    this.createCollectionBidModalActive = true;
  }

  onCopyHighestBidConfirmationCancelled(): void {
    this.copyConfirmationModalActive = false;
    this.copyConfirmationPunkIds = [];
    this.copyFromBidPayload = null;
  }

  // Targeted bid selection methods
  toggleTargetedBidMode(): void {
    this.isTargetedBidMode = !this.isTargetedBidMode;
    if (!this.isTargetedBidMode) {
      this.selectedPunkIds = [];
      this.selectedPunkIdsSet.clear();
    }
    this.updateComputedProperties();
  }

  cancelTargetedBidMode(): void {
    this.isTargetedBidMode = false;
    this.selectedPunkIds = [];
    this.selectedPunkIdsSet.clear();
    this.updateComputedProperties();
  }

  clearSelection(): void {
    this.selectedPunkIds = [];
    this.selectedPunkIdsSet.clear();
    this.selectedPunksOverflowPanelOpen = false;
    this.updateComputedProperties();
  }

  toggleSelectedPunksOverflowPanel(): void {
    if (this.selectedPunkIdsCartOverflow.length === 0) {
      return;
    }
    this.selectedPunksOverflowPanelOpen = !this.selectedPunksOverflowPanelOpen;
  }

  togglePunkSelection(punkId: number): void {
    const index = this.selectedPunkIds.indexOf(punkId);
    if (index > -1) {
      this.selectedPunkIds.splice(index, 1);
      this.selectedPunkIdsSet.delete(punkId);
    } else {
      this.selectedPunkIds.push(punkId);
      // Keep sorted for consistency
      this.selectedPunkIds.sort((a, b) => a - b);
      this.selectedPunkIdsSet.add(punkId);
    }
    // Update computed properties after selection change
    this.updateComputedProperties();
  }

  isPunkSelected(punkId: number): boolean {
    return this.selectedPunkIdsSet.has(punkId);
  }

  handlePunkClick(item: Punk, event: Event): void {
    if (this.isTargetedBidMode) {
      event.preventDefault();
      event.stopPropagation();
      const punkId = parseInt(item.id);
      this.togglePunkSelection(punkId);
    }
    // If not in selection mode, allow normal navigation behavior
  }

  addPunkIdFromInput(): void {
    if (!this.punkIdInput || !this.punkIdInput.trim()) {
      return;
    }

    const punkId = parseInt(this.punkIdInput.trim());
    if (isNaN(punkId) || punkId < 0) {
      // Invalid input, clear it
      this.punkIdInput = '';
      return;
    }

    // Check for duplicates before adding using Set for O(1) lookup
    if (!this.selectedPunkIdsSet.has(punkId)) {
      this.selectedPunkIds.push(punkId);
      // Keep sorted for consistency
      this.selectedPunkIds.sort((a, b) => a - b);
      this.selectedPunkIdsSet.add(punkId);
      this.updateComputedProperties();
    }

    // Clear input after adding
    this.punkIdInput = '';
  }

  onPunkIdInputKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addPunkIdFromInput();
    }
  }

  /**
   * Add every punk matching the current grid filters to the custom bid selection.
   * On sales history, loads all remaining pages first (same as Remove Trait).
   */
  addAllDisplayedPunksToSelection(): void {
    if (
      !this.isTargetedBidMode ||
      !this.filteredPunks.length ||
      this.addAllBulkInProgress ||
      this.removeAllBulkInProgress
    ) {
      return;
    }
    void this.runAddAllFilteredToSelection();
  }

  private async runAddAllFilteredToSelection(): Promise<void> {
    this.addAllBulkInProgress = true;
    try {
      if (this.marketType === 'sales') {
        await this.ensureAllSalesPagesLoaded();
      }
      const source =
        this.marketType === 'sales'
          ? this.getSalesPipelineSourcePunks()
          : this.filteredPunks;
      const punkIds = this.getAllFilteredViewPunkIdsFromSource(source);
      let added = false;
      for (const punkId of punkIds) {
        if (!this.selectedPunkIdsSet.has(punkId)) {
          this.selectedPunkIds.push(punkId);
          this.selectedPunkIdsSet.add(punkId);
          added = true;
        }
      }
      if (added) {
        this.selectedPunkIds.sort((a, b) => a - b);
      }
    } catch (err) {
      console.error('[PunkGridViewComponent] runAddAllFilteredToSelection failed', err);
    } finally {
      this.addAllBulkInProgress = false;
      this.updateComputedProperties();
    }
  }

  /**
   * Remove every punk that matches the current grid filters from the custom bid selection.
   * On sales history, loads all remaining pages first so removal matches the full filtered set.
   */
  removeDisplayedPunksFromSelection(): void {
    if (
      this.removeAllDisplayedPunksDisabled ||
      this.removeAllBulkInProgress ||
      this.addAllBulkInProgress
    ) {
      return;
    }
    void this.runRemoveAllFilteredFromSelection();
  }

  private async runRemoveAllFilteredFromSelection(): Promise<void> {
    this.removeAllBulkInProgress = true;
    try {
      if (this.marketType === 'sales') {
        await this.ensureAllSalesPagesLoaded();
      }
      const source =
        this.marketType === 'sales'
          ? this.getSalesPipelineSourcePunks()
          : this.filteredPunks;
      const toRemove = new Set(this.getAllFilteredViewPunkIdsFromSource(source));
      if (toRemove.size === 0) {
        return;
      }
      this.selectedPunkIds = this.selectedPunkIds.filter((id) => !toRemove.has(id));
      this.selectedPunkIdsSet.clear();
      for (const id of this.selectedPunkIds) {
        this.selectedPunkIdsSet.add(id);
      }
    } catch (err) {
      console.error('[PunkGridViewComponent] runRemoveAllFilteredFromSelection failed', err);
    } finally {
      this.removeAllBulkInProgress = false;
      this.updateComputedProperties();
    }
  }

  isPunksmarketNativeSale(item: Punk): boolean {
    return (
      environment.collection === 'cryptopunks-v1' && isPunksmarketNativeSale(item)
    );
  }

  punksmarketPunkUrl(tokenId: string): string {
    return punksmarketAppPunkUrl(tokenId);
  }
}
